import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { isSearchEnabled } from 'lib/search/searchUtils'
import HeadData from 'components/Common/HeadData'
import { LUXURY_ESCAPES } from 'constants/brands'
import { ANYWHERE_PLACE_ID, ANYWHERE_NAME, TYPE_PAGE_LIMIT } from 'constants/search'
import config from 'constants/config'
import { fetchListBanners } from 'actions/ContentActions'
import { fetchHotelPopularHolidayTypes } from 'actions/DestinationAlertsActions'
import { BANNER_LOCATION_HOTELS_AND_RESORTS } from 'constants/bannerLocations'
import isAnyFilterApplied from 'lib/offer/filtersUtils'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_VILLA } from 'constants/offer'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelPageSearchHero from '../Hotels/HotelPageSearchHero'
import useHotelAnalytics from '../Hotels/hooks/useHotelAnalytics'
import { usePlaceByName } from 'hooks/usePlace'
import HotelOfferCarousel from 'home/components/OfferCarousels/HotelOfferCarousel'
import { PlacesByPlaceId, getPlaceByRegionCode } from 'constants/places'
import { GlobalSearchTrackingProvider } from 'contexts/GlobalSearch/GlobalSearchTracking'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'

interface MappedStateProps {
  trendingDestinations: Array<App.TrendingDestination>
  filters: App.OfferListFilters
  regionCode: string
  geoStateName?: string
  isSearchEnabled: boolean
}

interface MappedDispatchProps {
  fetchListBanners: Utils.ActionDispatcher<typeof fetchListBanners>
}

// NB: This filter is more narrowly scoped than the one defined in `src/client/constants/offerList`.
// Since svc-search#5577, all `rental` offers are automatically tagged as `'Homes & Villas'`. The
// hotels team will ensure that relevant bedbank properties (including `UNIQUE_STAYS`) are
// appropriately tagged.
const HOMES_AND_VILLAS_FILTERS: App.OfferListFilters = {
  offerTypes: [OFFER_TYPE_BED_BANK, OFFER_TYPE_VILLA],
  holidayTypes: ['Homes & Villas'],
}

const villaViewAllUrl = `/search/homes-and-villas?destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&adults=2&children=none`
const rentalsFilterCarouselDestinations: Array<{ label: string, value: string }> = [
  { label: 'Indonesia', value: 'le_43ec517d68b6edd3015b3edc9a11367b' },
  { label: 'Thailand', value: 'le_8f85517967795eeef66c225f7883bdcb' },
  { label: 'Gold Coast', value: 'le_bac732346917b0a1231354ef55faf00a' },
  { label: 'Japan', value: 'le_7647966b7343c29048673252e490f736' },
  { label: 'Italy', value: 'le_93db85ed909c13838ff95ccfa94cebd9' },
  { label: 'Spain', value: 'le_7e7757b1e12abcb736ab9a754ffb617a' },
  { label: 'France', value: 'le_093f65e080a295f8076b1c5722a46aa2' },
  { label: 'United Kingdom', value: 'le_cfecdb276f634854f3ef915e2e980c31' },
  { label: 'South Africa', value: 'le_9766527f2b5d3e95d4a733fcfb77bd7e' },
]
const rentalsFilterDefaultDestinationId = rentalsFilterCarouselDestinations[0]?.value

function RentalsPage({
  filters,
  regionCode,
  isSearchEnabled,
  fetchListBanners,
  geoStateName,
}: MappedStateProps & MappedDispatchProps) {
  const intl = useIntl()
  const isManualSortingEnabled = isManualSortingEnabledFor('hotels')
  useEffect(() => {
    fetchListBanners(BANNER_LOCATION_HOTELS_AND_RESORTS)
  }, [fetchListBanners])

  const dispatch = useAppDispatch()
  const hasFilters = isAnyFilterApplied(filters)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext()

  const {
    onSearchEvent,
    onHomesVillasHotelListEvent,
    onWeekendHotelListEvent,
  } = useHotelAnalytics()

  const headTitle = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: '786m3ESN637nlPJkjnMc',
        defaultMessage: 'Luxury Homes & Villas for Your Next Escape',
        description: 'Page title for luxury rentals page on the villa or rental property page',
      })
    : intl.formatMessage({
        id: 'wL-_gz_PrfojAyuUUrBv',
        defaultMessage: 'Homes & Villas',
        description: 'Page title for the rentals page (non-luxury brand)',
      })
  const description = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: '4lgujMU1H46wBL-4m6g3',
        defaultMessage: 'Book entire homes and apartments designed for togetherness. Self\u2010cater, spread out, and make your stay feel like home.',
        description: 'Meta description for luxury rentals page on the rental page',
      })
    : undefined

  const finalFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !hasFilters && !filters.sortBy) {
      return {
        ...HOMES_AND_VILLAS_FILTERS,
        sortBy: 'vertical-hotel',
        limit: TYPE_PAGE_LIMIT,
      }
    }

    return {
      ...filters,
      ...HOMES_AND_VILLAS_FILTERS,
      frontPageVisibleOnly: true,
      limit: TYPE_PAGE_LIMIT,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters, hasFilters, isManualSortingEnabled])
  const geoStatePlace = usePlaceByName(geoStateName)

  const userPlace = useMemo(() => {
    // if regionCode is AU, use geoStatePlace if it exists
    if (regionCode === 'AU' && geoStatePlace) {
      return geoStatePlace
    }
    return getPlaceByRegionCode(regionCode)
  }, [geoStatePlace, regionCode])

  const userPlaceViewURL = `/search/homes-and-villas?adults=2&children=none&destinationId=${userPlace.id}&destinationName=${userPlace.name}`
  const rentalsFilterCarouselFilters = useMemo<App.OfferListFilters>(() => ({
    ...HOMES_AND_VILLAS_FILTERS,
    frontPageVisibleOnly: true,
    limit: MAX_CAROUSEL_ITEM_COUNT,
    sortBy: 'homepage',
    destinationId: rentalsFilterDefaultDestinationId,
  }), [])
  const buildRentalsFilterAllLink = useMemo(() => {
    return (selectedFilter?: string) => {
      if (!selectedFilter) {
        return villaViewAllUrl
      }
      const [pathname, queryString = ''] = villaViewAllUrl.split('?')
      const params = new URLSearchParams(queryString)
      const selectedPlace = PlacesByPlaceId[selectedFilter]
      params.set('destinationId', selectedFilter)
      if (selectedPlace?.name) {
        params.set('destinationName', selectedPlace.name)
      }
      const nextQuery = params.toString()
      return nextQuery ? `${pathname}?${nextQuery}` : pathname
    }
  }, [])
  const rentalsHeroTitle = intl.formatMessage({
    id: 'UBmk9sEGA4krko7GDQ3L',
    defaultMessage: 'Homes & Villas',
    description: 'Hero banner title for the homes and villas rental properties listing page',
  })

  const rentalsHeroSubtitle = intl.formatMessage({
    id: 'ITLUkBqJKqvsFGYYc-LB',
    defaultMessage: 'Choose from hundreds of handpicked stays',
    description: 'Hero subtitle for rentals page on the villa or rental property page',
  })

  const rentalsHeroCarouselTitle = intl.formatMessage(
    {
      id: 'pIQ5PY_DIyDAOieIMQAi',
      defaultMessage: 'Discover <i>weekend getaways</i>',
      description: 'Title for weekend getaways carousel on rentals page with italic styling',
    },
    { i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i> },
  )

  const rentalsFilterCarouselTitle = intl.formatMessage(
    {
      id: 'Ls8ZmFBsbJe0jIutj_K3',
      defaultMessage: 'Trending destinations',
      description: 'Title for trending destinations carousel on rentals page',
    },
  )

  const flashOfferList = useOfferList(finalFilters)
  useOfferListFilters(rentalsFilterCarouselFilters)

  useEffect(() => {
    dispatch(fetchHotelPopularHolidayTypes())
  }, [dispatch])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <div>
      <HeadData title={intl.formatMessage({ id: '9FmjRvcemhOmRtzLzzhu', defaultMessage: '{headTitle} - {siteTitle} {regionCode}', description: 'SEO page title for rentals page combining page title, site name and region code' }, { headTitle, siteTitle: config.title, regionCode })} description={description} />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        {(isSearchEnabled &&
          <GlobalSearchTrackingProvider onEvent={onSearchEvent}>
            <HotelPageSearchHero
              floatingSearchBar={!flashOfferList.fetching && !flashOfferList.offerIds.length}
              useInlineSearchBar
              useCompactMobileExpandedSpacing
              searchBasePath="/search/homes-and-villas"
              disableNaturalLanguageSearch
              heroTitle={rentalsHeroTitle}
              heroSubtitle={rentalsHeroSubtitle}
              heroImageId="rentals-landing-page-canada"
              useDropShadow
            />
          </GlobalSearchTrackingProvider>)}
        <OfferListEventsProvider onListEvent={onWeekendHotelListEvent}>
          <HotelOfferCarousel
            title={rentalsHeroCarouselTitle}
            offerTypes={[OFFER_TYPE_VILLA]}
            viewAllUrl={userPlaceViewURL}
            destinationId={userPlace.id}
            hideFilters
          />
        </OfferListEventsProvider>
      </GlobalSearchContextProvider>
      <OfferListEventsProvider onListEvent={onHomesVillasHotelListEvent}>
        <OfferCardCarouselSection
          filters={rentalsFilterCarouselFilters}
          title={rentalsFilterCarouselTitle}
          allLink={villaViewAllUrl}
          buildAllLink={buildRentalsFilterAllLink}
          additionalFilters="destinations"
          additionalFilterOptions={rentalsFilterCarouselDestinations}
          maxFilterOptions={rentalsFilterCarouselDestinations.length}
          defaultFilter={rentalsFilterDefaultDestinationId}
          tileStyle="card-default"
          tileSize="medium"
          data-testid="rentals-filter-carousel"
        >
          <OfferCarouselLookingForSomethingElse to={villaViewAllUrl} />
        </OfferCardCarouselSection>
      </OfferListEventsProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  trendingDestinations: state.destination.trendingDestinations,
  filters: getUrlOfferListFilters(state),
  regionCode: state.geo.currentRegionCode,
  geoStateName: state.geo.geoStateName,
  isSearchEnabled: isSearchEnabled(),
})

const mapDispatchToProps: MappedDispatchProps = {
  fetchListBanners,
}

export default connect(mapStateToProps, mapDispatchToProps)(RentalsPage)
